import React, { useState, useEffect } from "react";
import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import Layout from "../components/Layout";
import { MdRefresh, MdFileDownload, MdCheckCircle, MdError } from "react-icons/md";
import { FaTruck, FaMapMarkerAlt } from "react-icons/fa";

// --- PREMIUM UI TOKENS ---
const UI_THEME = {
  bg: "#f0f2f5",
  surface: "#ffffff",
  primary: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
  secondary: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
  danger: "#ef4444",
  success: "#10b981",
  warning: "#f59e0b",
  text: "#1e293b",
  muted: "#64748b",
  border: "#e2e8f0",
  shadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
};

const EInvoiceGenerator = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [companyInfo, setCompanyInfo] = useState(null);
  const [statusMap, setStatusMap] = useState({}); 
  const [skuMap, setSkuMap] = useState({});

  useEffect(() => {
    fetchCompanyInfo();
    fetchSkuMaster();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const snap = await getDoc(doc(db, "companyInfo", "default"));
      if (snap.exists()) setCompanyInfo(snap.data());
    } catch (err) {
      console.error("Error fetching company info:", err);
    }
  };

  const fetchSkuMaster = async () => {
    const snap = await getDocs(collection(db, "skuMaster"));
    const sm = {};
    snap.forEach(d => {
        const data = d.data();
        sm[data.sku] = data;
    });
    setSkuMap(sm);
  };

  const fetchOrders = async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, "outletOrders"),
        where("approvedDateOnly", "==", selectedDate)
      );
      const snap = await getDocs(q);
      let docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs = docs.filter(o => o.status !== "CANCELLED");

      const outletCache = {};
      const patchedDocs = await Promise.all(docs.map(async (o) => {
        let city = o.city;
        let pincode = o.pincode;
        let gstin = (o.gstno || o.gstin || "").trim();
        let address = o.address;
        let phone = o.phone;

        if (!city || !pincode || gstin.length !== 15 || !address) {
          const outletId = o.outletId;
          if (outletId) {
            if (!outletCache[outletId]) {
              const mSnap = await getDoc(doc(db, "outletMaster", outletId));
              if (mSnap.exists()) outletCache[outletId] = mSnap.data();
            }
            const master = outletCache[outletId];
            if (master) {
              city = city || master.city || "";
              pincode = pincode || master.pincode || "";
              gstin = gstin || master.gstin || "";
              address = address || master.address || "";
              phone = phone || master.phone || "";
            }
          }
        }

        const lines = o.lines || o.orderLines || [];
        return {
          ...o,
          city,
          pincode,
          gstin,
          address,
          phone,
          stateCode: o.stateCode || "29",
          lines: lines
        };
      }));

      const gstOnlyDocs = patchedDocs.filter(o => (o.gstin || "").length === 15);
      setOrders(gstOnlyDocs);
      validateOrders(gstOnlyDocs);
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const validateOrders = (orderList) => {
    const newStatusMap = {};
    orderList.forEach(o => {
      const errors = [];
      if (!o.gstin || o.gstin.length !== 15) errors.push("Invalid Buyer GSTIN");
      if (!o.pincode) errors.push("Missing Pincode");
      if (!o.city) errors.push("Missing City");
      const lines = o.lines || [];
      if (lines.length === 0) errors.push("No Items");
      lines.forEach((l, idx) => { if (!l.hsn) errors.push(`Item ${idx+1}: No HSN`); });
      newStatusMap[o.id] = errors;
    });
    setStatusMap(newStatusMap);
  };

  const round2 = (num) => Math.round((Number(num) + Number.EPSILON) * 100) / 100;

  const cleanDocNo = (no) => {
    if (!no) return "";
    let cleaned = no.replace(/[^a-zA-Z0-9\-\/]/g, "").replace(/_/g, "");
    while (cleaned.length > 0 && (cleaned.startsWith("0") || cleaned.startsWith("-") || cleaned.startsWith("/"))) {
      cleaned = cleaned.substring(1);
    }
    return cleaned;
  };

  const generateJson = (o) => {
    if (!companyInfo) return;
    const docNo = cleanDocNo(o.displayOrderId || o.id);
    const [year, month, day] = o.approvedDateOnly.split("-");
    const formattedDate = `${day}/${month}/${year}`;

    const itemList = o.lines.map((l, idx) => {
      const sku = skuMap[l.sku] || {};
      const qty = Number(l.qty || 0);
      const rate = round2(l.rate || l.basicRate || 0);
      const assAmt = round2(l.basicAmount || (qty * rate));
      const cgst = round2(l.gstAmount / 2 || 0);
      const sgst = round2(l.gstAmount / 2 || 0);

      return {
        SlNo: String(idx + 1),
        PrdDesc: l.skuName || l.description || "Product",
        IsServc: "N",
        HsnCd: String(l.hsn || ""),
        Qty: qty,
        FreeQty: 0,
        Unit: sku.unitType || "THD",
        UnitPrice: rate,
        TotAmt: assAmt,
        Discount: 0,
        PreTaxVal: assAmt,
        AssAmt: assAmt,
        GstRt: Number(l.gstPercent || 0),
        CesRt: 0,
        IgstAmt: 0,
        CgstAmt: cgst,
        SgstAmt: sgst,
        CesAmt: 0,
        CesNonAdvlAmt: 0,
        StateCesRt: 0,
        StateCesAmt: 0,
        StateCesNonAdvlAmt: 0,
        OthChrg: 0,
        TotItemVal: round2(assAmt + cgst + sgst)
      };
    });

    const totalAssVal = round2(itemList.reduce((sum, item) => sum + item.AssAmt, 0));
    const totalCgst = round2(itemList.reduce((sum, item) => sum + item.CgstAmt, 0));
    const totalSgst = round2(itemList.reduce((sum, item) => sum + item.SgstAmt, 0));

    const payload = [{
      Version: "1.1",
      TranDtls: { TaxSch: "GST", SupTyp: "B2B", RegRev: "N", IgstOnIntra: "N" },
      DocDtls: { Typ: "INV", No: docNo, Dt: formattedDate },
      SellerDtls: {
        Gstin: companyInfo.gstin || "",
        LglNm: companyInfo.name || "",
        Addr1: companyInfo.address1 || "",
        Loc: companyInfo.place || "",
        Pin: Number(companyInfo.pincode || 0),
        Stcd: String(companyInfo.statecode || "29")
      },
      BuyerDtls: {
        Gstin: o.gstin,
        LglNm: o.outletName || "",
        Pos: String(o.stateCode || "29"),
        Addr1: o.address || "",
        Loc: o.city || "Unknown",
        Stcd: String(o.stateCode || "29"),
        Pin: Number(o.pincode || 0)
      },
      ValDtls: {
        AssVal: totalAssVal,
        CgstVal: totalCgst,
        SgstVal: totalSgst,
        IgstVal: 0,
        CesVal: 0,
        StCesVal: 0,
        Discount: 0,
        OthChrg: 0,
        RndOffAmt: 0,
        TotInvVal: round2(totalAssVal + totalCgst + totalSgst)
      },
      ItemList: itemList
    }];

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `EInvoice-${docNo}.json`;
    a.click();
  };

  const thStyle = { padding: "16px 20px", fontSize: "11px", fontWeight: 800, color: UI_THEME.muted, textTransform: "uppercase", borderBottom: "2px solid " + UI_THEME.border };
  const tdStyle = { padding: "16px 20px", fontSize: "13px", color: UI_THEME.text };

  return (
    <Layout>
      <div style={{ padding: "32px", background: UI_THEME.bg, minHeight: "100vh" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "32px" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: "28px", fontWeight: 800, color: UI_THEME.text }}>Registered Party Center</h1>
            <p style={{ margin: "4px 0 0 0", color: UI_THEME.muted, fontWeight: 500 }}>Generate E-Invoice & E-Way Bill for B2B Clients</p>
          </div>
        </div>

        <div style={{ background: "#fff", padding: "24px", borderRadius: "16px", border: "1px solid " + UI_THEME.border, marginBottom: "32px", display: "flex", gap: "20px", alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: "11px", fontWeight: 800, color: UI_THEME.muted, marginBottom: "8px" }}>Invoice Date</label>
            <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={{ width: "100%", padding: "12px", borderRadius: "10px", border: "1px solid " + UI_THEME.border, fontWeight: 600 }} />
          </div>
          <button onClick={fetchOrders} disabled={loading} style={{ padding: "12px 30px", background: "#f1f5f9", color: "#475569", border: "none", borderRadius: "10px", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
            <MdRefresh size={20} /> {loading ? "Scanning..." : "Fetch Orders"}
          </button>
        </div>

        <div style={{ background: "#fff", borderRadius: "16px", border: "1px solid " + UI_THEME.border, overflow: "hidden", boxShadow: UI_THEME.shadow }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={thStyle}>Order ID</th>
                <th style={thStyle}>Outlet</th>
                <th style={thStyle}>GSTIN</th>
                <th style={thStyle}>City / Pin</th>
                <th style={thStyle}>Items</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Audit</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr><td colSpan="8" style={{ padding: "60px", textAlign: "center", color: UI_THEME.muted }}>No registered orders found.</td></tr>
              ) : orders.map(o => {
                const errors = statusMap[o.id] || [];
                const isReady = errors.length === 0;
                return (
                  <tr key={o.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{o.displayOrderId}</td>
                    <td style={tdStyle}>{o.outletName}</td>
                    <td style={tdStyle}>{o.gstin}</td>
                    <td style={tdStyle}>{o.city} / {o.pincode}</td>
                    <td style={tdStyle}>{o.lines?.length} SKUs</td>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>₹{Number(o.totalAmount || 0).toLocaleString()}</td>
                    <td style={tdStyle}>
                      {isReady ? <div style={{ color: UI_THEME.success, fontWeight: 700, fontSize: "11px" }}>READY</div> : <div style={{ color: UI_THEME.danger, fontSize: "10px" }}>{errors[0]}</div>}
                    </td>
                    <td style={{ ...tdStyle }}>
                      <button onClick={() => generateJson(o)} disabled={!isReady} style={{ padding: "8px 16px", background: isReady ? UI_THEME.primary : "#e2e8f0", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 700, cursor: isReady ? "pointer" : "not-allowed", fontSize: "11px", display: "flex", alignItems: "center", gap: "6px" }}>
                        <MdFileDownload size={16} /> JSON
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default EInvoiceGenerator;
